// ImportUIHelper.ts
import { Resource as CommonResource } from "@/models/Message/Resource/CommonResource";
import { MessageUtil } from '@/models/Message/MessageUtil';
import { useMessageBox } from "lib-vue-v2/composables";
import { CsvError, CsvErrorCode, HeaderSpecItem } from "./CsvTypes";
import { ImportResult, CsvImportResultDef, ImportErrorDisplayInfo, ImportErrorInfo, ImportContentErrorInfo } from "./ImportResultModel";

/** メッセージダイアログ */
const { showMessageBox } = useMessageBox();

// CsvErrorCodePriorityMap(小さいほうが優先される)
const CsvErrorCodePriorityMap: ReadonlyMap<CsvErrorCode, number> = new Map([
    // file全体
    [CsvErrorCode.EMPTY, 0],
    [CsvErrorCode.FILE_NOT_CSV, 1],
    [CsvErrorCode.FILE_TOO_LARGE, 2],
    [CsvErrorCode.INVALID_ENCODING, 3],
    // header
    [CsvErrorCode.HEADER_MISSING, 1000],
    [CsvErrorCode.HEADER_DUPLICATED, 1001],
    // common
    [CsvErrorCode.PARSE_ERROR, 2000],
    [CsvErrorCode.QUOTE_NOT_CLOSED, 2001]
]);

/**
 * エラー情報からエラーメッセージを表示する
 * @param errors CSVエラー
 */
export function showImportErrorMessage(errors: CsvError[]) {
    if (errors.length === 0) return;
    const sortedErrors = sortCsvErrorsByPriority(errors);

    const commonResource = CommonResource.CommonResource.getInstance();
    // TOP1のもののみ出力する
    const displayError = sortedErrors[0];
    switch (displayError.code) {
        case CsvErrorCode.EMPTY:
            showMessageBox(commonResource.getMsgInfo(CommonResource.MsgID.Import_DataNotExistWarn));
            break;
        case CsvErrorCode.HEADER_MISSING:
            showMessageBox(commonResource.getMsgInfo(CommonResource.MsgID.Import_RequiredHeaderNotExistWarn, [connectErrorHeader(displayError.detail)]));
            break;
        case CsvErrorCode.HEADER_DUPLICATED:
            showMessageBox(commonResource.getMsgInfo(CommonResource.MsgID.Import_RequiredHeaderDuplicateWarn, [connectErrorHeader(displayError.detail)]));
            break;
        // 必要に応じて追加してください
        default:
            showMessageBox(commonResource.getMsgInfo(CommonResource.MsgID.Import_FailedError));
            break;
    }
}

// エラー情報をsortする
function sortCsvErrorsByPriority(errors: readonly CsvError[]) {
    const LOWEST_PRIORITY = Infinity;
    return [...errors].sort((a, b) => {
        const priorityA = CsvErrorCodePriorityMap.get(a.code) ?? LOWEST_PRIORITY;
        const priorityB = CsvErrorCodePriorityMap.get(b.code) ?? LOWEST_PRIORITY;
        return priorityA - priorityB;
    });
}

// 異常ヘッダの情報をつなげる
function connectErrorHeader(headeNames?: string[]): string {
    if (headeNames === undefined) return "";
    const info = headeNames.map(headeName => `・${headeName}`);
    return info.join("<br/>");
}

/**
 * ヘッダ解析エラーから、表示用HTMLモデル(ImportErrorDisplayInfo)を生成する
 */
export function createHeaderErrorMessage(headerSpec: HeaderSpecItem[],
    errors: CsvError[],
    importResult: ImportResult
): ImportErrorDisplayInfo {
    // errors配列の存在は呼びもとが保証する
    const sortedErrors = sortCsvErrorsByPriority(errors);
    const err = sortedErrors[0];
    switch (err.code) {
        case CsvErrorCode.EMPTY:
            importResult.result = CsvImportResultDef.EmptyFile;
            break;
        case CsvErrorCode.HEADER_MISSING:
            {
                const importErrorInfo = new ImportErrorInfo();
                importErrorInfo.line = 0;
                const importContentErrorInfo = new ImportContentErrorInfo();
                importContentErrorInfo.errorDetail.push(...headerSpec.filter(x => x.required).map(x => x.name));
                importErrorInfo.info.push(importContentErrorInfo);
                importResult.result = CsvImportResultDef.MissingRequiredHeader;
                (importResult.errors ??= []).push(importErrorInfo);
            }
            break;
        case CsvErrorCode.HEADER_DUPLICATED:
            {
                const importErrorInfo = new ImportErrorInfo();
                importErrorInfo.line = 0;
                const importContentErrorInfo = new ImportContentErrorInfo();
                importContentErrorInfo.errorDetail.push(...(err.detail ?? []));
                importErrorInfo.info.push(importContentErrorInfo);
                importResult.result = CsvImportResultDef.DuplicateHeader;
                (importResult.errors ??= []).push(importErrorInfo);
            }
            break;
        default:
            importResult.result = CsvImportResultDef.GeneralFailure;
            break;
    }
    importResult.endTimeUTC = formatedDateTime();
    return createImportErrorInfo(importResult, "");
}

/**
 * ImportResultのステータスから、表示用HTMLモデル(ImportErrorDisplayInfo)を生成する
 */
export function createImportErrorInfo(
    importResult: ImportResult,
    limitOverMessage: string
): ImportErrorDisplayInfo {
    if (importResult.result === CsvImportResultDef.EmptyFile || importResult.result === CsvImportResultDef.NoData) {
        return buildEmptyError();
    }
    else if (importResult.result === CsvImportResultDef.LimitOver) {
        return buildLimitOverError(limitOverMessage);
    }
    else if (importResult.result === CsvImportResultDef.MissingRequiredHeader && importResult.errors) {
        return buildMissingHeaderError(importResult.errors);
    }
    else if (importResult.result === CsvImportResultDef.DuplicateHeader && importResult.errors) {
        return buildDuplicateHeaderError(importResult.errors);
    }
    else if (importResult.result === CsvImportResultDef.ContentIssue && importResult.errors) {
        return buildContentError(importResult.startTimeUTC, importResult.endTimeUTC, importResult.errors);
    }
    else {
        return {
            strHTML: "インポートに失敗しました。",
            bit: MessageUtil.MB_ICON_ERROR
        };
    }
}

function buildEmptyError(): ImportErrorDisplayInfo {
    let str_html = "インポートデータが見つかりません。ファイルの内容を確認してください。";

    return {
        strHTML: str_html,
        bit: MessageUtil.MB_ICON_WARNING
    };
}

function buildLimitOverError(limitOverMessage: string): ImportErrorDisplayInfo {
    let str_html = limitOverMessage;

    return {
        strHTML: str_html,
        bit: MessageUtil.MB_ICON_WARNING
    };
}

function buildMissingHeaderError(importErrorInfo: ImportErrorInfo[]): ImportErrorDisplayInfo {
    let messages = [];
    messages.push("インポートデータが見つかりません。以下のヘッダが必要です。<br />");

    importErrorInfo.forEach(error => {
        error.info.forEach(info => {
            info.errorDetail.forEach(detail => {
                messages.push(`・${detail}`);
            })
        })
    })

    return {
        strHTML: messages.join("<br/>"),
        bit: MessageUtil.MB_ICON_WARNING
    };
}

function buildDuplicateHeaderError(importErrorInfo: ImportErrorInfo[]): ImportErrorDisplayInfo {
    let messages = [];
    messages.push("インポートデータでは以下のヘッダ名が重複しているため、インポートできません。<br />");

    importErrorInfo.forEach(error => {
        error.info.forEach(info => {
            info.errorDetail.forEach(detail => {
                messages.push(`・${detail}`);
            })
        })
    })

    return {
        strHTML: messages.join("<br/>"),
        bit: MessageUtil.MB_ICON_WARNING
    };
}

function buildContentError(startTimeUTC: string | null, endTimeUTC: string | null, importErrorInfo: ImportErrorInfo[]): ImportErrorDisplayInfo {
    let messages = [];
    if (startTimeUTC) {
        let tmpDate = new Date(startTimeUTC);
        let dateLocalTime = new Date(tmpDate.toLocaleString());
        messages.push(`--- チェック開始 ${dateLocalTime.SystemtimeToStr()} ---`);
        messages.push("");
    }

    importErrorInfo.forEach(error => {
        messages.push(`${(error.line).toString()}行目に問題があります。`);
        error.info.forEach(info => {
            info.errorDetail.forEach(detail => {
                if (info.property) messages.push(`\t${info.property}が不正です。${detail}`);
                else messages.push(`\t${detail}`);
            })
        })
        messages.push("");
    })

    if (endTimeUTC) {
        let tmpDate = new Date(endTimeUTC);
        let dateLocalTime = new Date(tmpDate.toLocaleString());
        messages.push(`--- チェック完了 ${dateLocalTime.SystemtimeToStr()} ---`);
    }

    return {
        strHTML: messages.join("\n"),
        bit: MessageUtil.MB_ICON_WARNING
    };
}

// 日付フォーマット
export function formatedDateTime(target: Date = new Date()): string {
    const year = target.getFullYear();
    const month = ('0' + (target.getMonth() + 1)).slice(-2);
    const day = ('0' + target.getDate()).slice(-2);
    const hours = ('0' + target.getHours()).slice(-2);
    const minutes = ('0' + target.getMinutes()).slice(-2);
    const seconds = ('0' + target.getSeconds()).slice(-2);
    const milliseconds = ('00' + target.getMilliseconds()).slice(-3);
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
}